import {gql} from '@apollo/client';
import React from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {
  ProcessMetadataSectionQuery,
  ProcessMetadataSectionQueryVariables,
} from './__generated/ProcessMetadataSectionQuery';

type Process = ExtractByTypename<ProcessMetadataSectionQuery['process'], 'ProcessNode'>;

type ProcessTaskSchema = Process['processTaskSchema'];

export const PROCESS_METADATA_SECTION_QUERY = gql`
  query ProcessMetadataSectionQuery($processId: StrictString!) @skipAudit @engComponent(name: WORKFLOWS) {
    process(id: $processId) {
      ... on ProcessNode {
        id
        processTaskSchema {
          id
          name
          archiveRecord {
            purpose
          }
        }
        watcherParties {
          ... on UserNode {
            id
            name
            handle
          }
          ... on TeamNode {
            id
            name
            handle
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function ProcessTaskSchemaField(props: {processTaskSchema: ProcessTaskSchema | null}): JSX.Element {
  if (props.processTaskSchema == null) {
    return <HorizontalThrobber />;
  }

  return (
    <span>
      {props.processTaskSchema.name}
      {props.processTaskSchema.archiveRecord && ' (archived)'}
    </span>
  );
}

function MetadataColumn(props: {children: JSX.Element; title: string}): JSX.Element {
  return (
    <Col sm={6}>
      <div className="metadata-section">
        <div className="metadata-header">{props.title}</div>
        {props.children}
      </div>
    </Col>
  );
}

function ProcessMetadataSection(props: {process: Process | null}): JSX.Element {
  return (
    <>
      <Row bufferRowReset={true}>
        <MetadataColumn title="Task schema">
          <ProcessTaskSchemaField processTaskSchema={props.process?.processTaskSchema ?? null} />
        </MetadataColumn>
      </Row>
    </>
  );
}

interface QueryingProcessMetadataSectionProps {
  processId: string;
}

export default function QueryingProcessMetadataSection(
  props: QueryingProcessMetadataSectionProps
): JSX.Element {
  return (
    <BenchlingQuery<ProcessMetadataSectionQuery, ProcessMetadataSectionQueryVariables>
      query={PROCESS_METADATA_SECTION_QUERY}
      variables={{processId: props.processId}}
      renderLoading={() => <ProcessMetadataSection process={null} />}
    >
      {(data) => {
        const {process} = data;
        if (isInaccessible(process)) {
          return <WorkspaceItemInaccessibleGuard inaccessibleInfo={process} modelType="process" />;
        }
        return <ProcessMetadataSection process={process} />;
      }}
    </BenchlingQuery>
  );
}
